import {
  Box,
  Breadcrumbs,
  Button,
  Link,
  Skeleton,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Home as HomeIcon } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { Org } from "~/lib/types";
import { setDocumentTitle } from "~/lib/commonUtils";
import { useCanSetOrganizationMembership } from "~/lib/iam/actions/mondoo/captain/Captain/SetOrganizationMembership";
import { AddUserDialog } from "~/components/Members/AddUserDialog";
import NoMembersIcon from "~/images/settings/NoMembersIcon";
import {
  columns,
  usersDataGridInitialState,
  usersDataGridSlotProps,
  usersDataGridSx,
  getRowClassName,
} from "./components";
import { useUsersList, useUsersFilterContext } from "./hooks";

export type UsersListPageProps = {
  org: Org;
};

export function UsersListPage({ org }: UsersListPageProps) {
  setDocumentTitle(["Users", org.name]);

  const canInviteMembers = useCanSetOrganizationMembership();

  const { filterVariables, filterForm, activeFilterCount } =
    useUsersFilterContext();

  const {
    addMemberOpen,
    openAddMemberDialog,
    closeAddMemberDialog,
    loading,
    rows,
    handleRowClick,
    paginationModel,
    onPaginationModelChange,
    rowCount,
  } = useUsersList(org, filterVariables.rolesFilter, filterVariables.query);

  const breadcrumbs = [
    <Link
      key="/organization/overview"
      component={RouterLink}
      to={`/organization/overview?organizationId=${org.id}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Typography key="/organization/users">Users</Typography>,
  ];

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3, overflowWrap: "anywhere" }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            textTransform="uppercase"
          >
            Users
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={150} height={24} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {rowCount} users in {org.name}
            </Typography>
          )}
        </Box>
        {canInviteMembers && (
          <Button
            variant="contained"
            color="primary"
            onClick={openAddMemberDialog}
            sx={{ height: 40 }}
          >
            Add User
          </Button>
        )}
      </Box>

      {/* Filter Form */}
      <Box mb={4}>{filterForm}</Box>

      {/* DataGrid Table or Empty State */}
      {!loading && rows.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            px: 2,
            textAlign: "center",
            bgcolor: "background.paper",
            borderRadius: 1,
            minHeight: 300,
          }}
        >
          <NoMembersIcon sx={{ width: "48px", height: "48px", mb: 1 }} />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              mb: 1,
            }}
          >
            {activeFilterCount > 0
              ? "No users found"
              : "This organization has no users"}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 1,
            }}
          >
            {activeFilterCount > 0
              ? "Your search did not match any users in this organization."
              : "There are currently no users in this organization."}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 3,
            }}
          >
            {activeFilterCount > 0
              ? "Try adjusting your search criteria."
              : "Start collaborating with other users by inviting them to join you."}
          </Typography>
          {activeFilterCount === 0 && canInviteMembers && (
            <Button
              variant="outlined"
              color="primary"
              onClick={openAddMemberDialog}
              sx={{
                textTransform: "uppercase",
                fontWeight: 500,
                px: 3,
              }}
            >
              Invite Member
            </Button>
          )}
        </Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          onRowClick={handleRowClick}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          rowCount={rowCount}
          disableRowSelectionOnClick
          disableColumnMenu
          columnHeaderHeight={48}
          rowHeight={64}
          pageSizeOptions={[10, 25, 50]}
          initialState={usersDataGridInitialState}
          getRowClassName={getRowClassName}
          slotProps={usersDataGridSlotProps}
          sx={usersDataGridSx}
        />
      )}

      {/* Add User Dialog */}
      <AddUserDialog
        resourceMrn={org.mrn}
        onClose={closeAddMemberDialog}
        dialogProps={{ open: addMemberOpen }}
      />
    </Box>
  );
}
